import { QualityObjectiveItem, QualityPlanForm } from '../../types/qualityPolicy';
import { emptyObjective } from '../../utils/qualityPolicyForms';
import QualityDocMetaFields from './QualityDocMetaFields';

interface Props {
  year: number;
  value: QualityPlanForm;
  disabled?: boolean;
  onChange: (next: QualityPlanForm) => void;
}

function QualityPlanFormView({ year, value, disabled, onChange }: Props) {
  const updateObjective = (id: string, patch: Partial<QualityObjectiveItem>) => {
    onChange({
      ...value,
      objectives: value.objectives.map((row) => (row.id === id ? { ...row, ...patch } : row)),
    });
  };

  const removeObjective = (id: string) => {
    const next = value.objectives.filter((row) => row.id !== id);
    onChange({ ...value, objectives: next.length ? next : [emptyObjective()] });
  };

  return (
    <div className="qform">
      <p className="qform-lead">
        {year}년 품질방침 추진 계획서 — 경영방침·사훈·대내외 환경, 품질방침문, 적용범위, 품질목표,
        추진일정을 작성합니다.
      </p>

      <QualityDocMetaFields
        meta={value.meta}
        disabled={disabled}
        onChange={(meta) => onChange({ ...value, meta })}
      />

      <div className="qform-section">
        <h3>경영방침</h3>
        <textarea
          rows={4}
          value={value.managementPolicy}
          disabled={disabled}
          onChange={(e) => onChange({ ...value, managementPolicy: e.target.value })}
          placeholder="회사 경영방침을 기술하세요."
        />
      </div>

      <div className="qform-section">
        <h3>사훈</h3>
        <textarea
          rows={2}
          value={value.companyMotto}
          disabled={disabled}
          onChange={(e) => onChange({ ...value, companyMotto: e.target.value })}
          placeholder="사훈"
        />
      </div>

      <div className="qform-section">
        <h3>대내외 환경</h3>
        <textarea
          rows={5}
          value={value.environmentAnalysis}
          disabled={disabled}
          onChange={(e) => onChange({ ...value, environmentAnalysis: e.target.value })}
          placeholder="대내·대외 환경 분석, 이해관계자·리스크·기회 등"
        />
      </div>

      <div className="qform-section">
        <h3>품질방침문</h3>
        <textarea
          rows={5}
          value={value.policyStatement}
          disabled={disabled}
          onChange={(e) => onChange({ ...value, policyStatement: e.target.value })}
          placeholder="회사 품질방침을 기술하세요."
        />
      </div>

      <div className="qform-section">
        <h3>적용 범위</h3>
        <textarea
          rows={3}
          value={value.scope}
          disabled={disabled}
          onChange={(e) => onChange({ ...value, scope: e.target.value })}
          placeholder="품질경영시스템 적용 범위"
        />
      </div>

      <div className="qform-section">
        <h3>품질조직 및 책임</h3>
        <textarea
          rows={4}
          value={value.organization}
          disabled={disabled}
          onChange={(e) => onChange({ ...value, organization: e.target.value })}
          placeholder="품질책임자, 부서별 역할·책임"
        />
      </div>

      <div className="qform-section">
        <div className="qform-section__head">
          <h3>품질목표</h3>
          {!disabled && (
            <button
              type="button"
              className="ncr-btn is-secondary"
              onClick={() => onChange({ ...value, objectives: [...value.objectives, emptyObjective()] })}
            >
              목표 추가
            </button>
          )}
        </div>
        <div className="qform-table-wrap">
          <table className="qform-table">
            <thead>
              <tr>
                <th>코드</th>
                <th>목표명</th>
                <th>목표값</th>
                <th>측정방법</th>
                <th>담당</th>
                <th>목표일</th>
                {!disabled && <th />}
              </tr>
            </thead>
            <tbody>
              {value.objectives.map((row) => (
                <tr key={row.id}>
                  <td>
                    <input
                      value={row.code}
                      disabled={disabled}
                      placeholder="QO-01"
                      onChange={(e) => updateObjective(row.id, { code: e.target.value })}
                    />
                  </td>
                  <td>
                    <input
                      value={row.title}
                      disabled={disabled}
                      placeholder="목표명"
                      onChange={(e) => updateObjective(row.id, { title: e.target.value })}
                    />
                  </td>
                  <td>
                    <input
                      value={row.target}
                      disabled={disabled}
                      placeholder="예: 불량률 0.5% 이하"
                      onChange={(e) => updateObjective(row.id, { target: e.target.value })}
                    />
                  </td>
                  <td>
                    <input
                      value={row.measure}
                      disabled={disabled}
                      placeholder="월별 집계 등"
                      onChange={(e) => updateObjective(row.id, { measure: e.target.value })}
                    />
                  </td>
                  <td>
                    <input
                      value={row.owner}
                      disabled={disabled}
                      onChange={(e) => updateObjective(row.id, { owner: e.target.value })}
                    />
                  </td>
                  <td>
                    <input
                      type="date"
                      value={row.dueDate}
                      disabled={disabled}
                      onChange={(e) => updateObjective(row.id, { dueDate: e.target.value })}
                    />
                  </td>
                  {!disabled && (
                    <td>
                      <button
                        type="button"
                        className="qform-row-del"
                        onClick={() => removeObjective(row.id)}
                      >
                        삭제
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="qform-section">
        <h3>연간 추진일정</h3>
        <textarea
          rows={4}
          value={value.annualSchedule}
          disabled={disabled}
          onChange={(e) => onChange({ ...value, annualSchedule: e.target.value })}
          placeholder="분기별 주요 추진 일정, 내부심사·경영검토 일정 등"
        />
      </div>

      <div className="qform-section">
        <h3>비고</h3>
        <textarea
          rows={3}
          value={value.remarks}
          disabled={disabled}
          onChange={(e) => onChange({ ...value, remarks: e.target.value })}
        />
      </div>
    </div>
  );
}

export default QualityPlanFormView;
